import React from 'react';

/** Compact SVG marks for the Playing Hub — chess-forward, not generic admin icons. */

function Mark({ children, className = '', title }) {
  return (
    <svg
      className={className}
      viewBox="0 0 32 32"
      width="1em"
      height="1em"
      fill="none"
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
    >
      {title ? <title>{title}</title> : null}
      {children}
    </svg>
  );
}

export function MarkKnight({ className }) {
  return (
    <Mark className={className}>
      <path
        fill="currentColor"
        d="M9.5 26.5h13c.8 0 1.4-.7 1.2-1.5l-1.1-4.2c-.2-.7-.8-1.2-1.5-1.2h-1.2l1.8-3.2c.4-.7.3-1.6-.3-2.1L17 10.5l1.2-2.1c.3-.5.1-1.2-.4-1.5L15.2 5.6c-.5-.3-1.1-.1-1.4.4L12.5 8.2 9.8 7.4c-.6-.2-1.2.2-1.3.8L8 11.5c-.1.6.3 1.2.9 1.3l2.1.4-1.6 2.8c-.4.7-.3 1.5.2 2.1l1.5 1.6H9.8c-.7 0-1.3.5-1.5 1.2L7.2 25c-.2.8.4 1.5 1.2 1.5h1.1z"
      />
      <circle cx="14.2" cy="11.2" r="1.1" fill="var(--hub-mark-eye, #f8fafc)" />
    </Mark>
  );
}

export function MarkSwords({ className }) {
  return (
    <Mark className={className}>
      <path stroke="currentColor" strokeWidth="2" strokeLinecap="round" d="M8 24L22 10M10 8l2 2M20 22l2 2" />
      <path stroke="currentColor" strokeWidth="2" strokeLinecap="round" d="M24 24L10 10M22 8l-2 2M12 22l-2 2" />
      <circle cx="16" cy="16" r="2.2" fill="currentColor" />
    </Mark>
  );
}

export function MarkTrophy({ className }) {
  return (
    <Mark className={className}>
      <path
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
        d="M11 8h10v5.5c0 3-2.2 5.5-5 5.5s-5-2.5-5-5.5V8z"
      />
      <path stroke="currentColor" strokeWidth="2" strokeLinecap="round" d="M11 10H8.5A2.5 2.5 0 006 12.5V13a3 3 0 003 3h1M21 10h2.5A2.5 2.5 0 0126 12.5V13a3 3 0 01-3 3h-1" />
      <path stroke="currentColor" strokeWidth="2" strokeLinecap="round" d="M16 19v3M12 26h8M13 23h6" />
    </Mark>
  );
}

export function MarkBoard({ className }) {
  return (
    <Mark className={className}>
      <rect x="6" y="6" width="20" height="20" rx="2" stroke="currentColor" strokeWidth="2" />
      <path fill="currentColor" d="M6 6h5v5H6zm10 0h5v5h-5zm-5 5h5v5h-5zm10 0h5v5h-5zM6 16h5v5H6zm10 0h5v5h-5zm-5 5h5v5h-5zm10 0h5v5h-5z" opacity=".35" />
    </Mark>
  );
}

export function MarkCoach({ className }) {
  return (
    <Mark className={className}>
      <circle cx="16" cy="11" r="4" stroke="currentColor" strokeWidth="2" />
      <path stroke="currentColor" strokeWidth="2" strokeLinecap="round" d="M8 25c1.5-4 4.2-6 8-6s6.5 2 8 6" />
      <path stroke="currentColor" strokeWidth="2" strokeLinecap="round" d="M20.5 8.5l3-2M22.5 12h3.5" />
    </Mark>
  );
}

export function MarkLive({ className }) {
  return (
    <Mark className={className}>
      <circle cx="16" cy="16" r="3" fill="currentColor" />
      <circle cx="16" cy="16" r="7" stroke="currentColor" strokeWidth="2" opacity=".55" />
      <circle cx="16" cy="16" r="11" stroke="currentColor" strokeWidth="1.5" opacity=".3" />
    </Mark>
  );
}

export function MarkChallenge({ className }) {
  return (
    <Mark className={className}>
      <path
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
        d="M8 22V10l8-4 8 4v12l-8 4-8-4z"
      />
      <path stroke="currentColor" strokeWidth="2" strokeLinecap="round" d="M16 12v8M12.5 16H19.5" />
    </Mark>
  );
}

export function MarkSettings({ className }) {
  return (
    <Mark className={className}>
      <circle cx="16" cy="18" r="5" stroke="currentColor" strokeWidth="2" />
      <path fill="currentColor" d="M14.2 7.2h3.6l.6 2.2 2-.9 1.8 1.8-.9 2 2.2.6v3.6l-2.2.6.9 2-1.8 1.8-2-.9-.6 2.2h-3.6l-.6-2.2-2 .9-1.8-1.8.9-2-2.2-.6v-3.6l2.2-.6-.9-2 1.8-1.8 2 .9.6-2.2z" opacity=".2" />
      <path stroke="currentColor" strokeWidth="2" d="M16 8v2.5M16 21.5V24M8.8 12.2l2.1 1.2M21.1 18.6l2.1 1.2M8.8 19.8l2.1-1.2M21.1 13.4l2.1-1.2" strokeLinecap="round" />
    </Mark>
  );
}

export function MarkPawn({ className, filled = false }) {
  return (
    <Mark className={className}>
      <path
        fill={filled ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="2"
        d="M16 6.5a3.2 3.2 0 110 6.4 3.2 3.2 0 010-6.4zM11.5 25.5h9c.7 0 1.2-.6 1-1.2l-1.4-4.1c-.2-.6-.8-1-1.4-1h-5.4c-.6 0-1.2.4-1.4 1l-1.4 4.1c-.2.6.3 1.2 1 1.2z"
      />
      <path stroke="currentColor" strokeWidth="2" strokeLinecap="round" d="M13 15.5h6" />
    </Mark>
  );
}

/** White / black / random king glyphs for color pickers */
export function PieceTileGlyph({ color = 'white', className = '' }) {
  if (color === 'random') {
    return (
      <span className={`playing-piece-glyph playing-piece-glyph--random ${className}`} aria-hidden>
        <span className="playing-piece-glyph__half playing-piece-glyph__half--w">♔</span>
        <span className="playing-piece-glyph__half playing-piece-glyph__half--b">♚</span>
      </span>
    );
  }
  const isWhite = color === 'white';
  return (
    <span
      className={`playing-piece-glyph playing-piece-glyph--${isWhite ? 'white' : 'black'} ${className}`}
      aria-hidden
    >
      {isWhite ? '♔' : '♚'}
    </span>
  );
}

const SHORTCUT_MARKS = {
  '/playing/challenge': MarkChallenge,
  '/playing/games': MarkBoard,
  '/playing/ai-coach': MarkCoach,
  '/playing/coach-board': MarkKnight,
  '/playing/live': MarkLive,
  '/playing/leaderboard': MarkTrophy,
};

export function ShortcutMark({ to, className }) {
  const Comp = SHORTCUT_MARKS[to] || MarkBoard;
  return <Comp className={className} />;
}
